import { createAction, createReducer } from 'redux-act';

import { SravniNewsSharedModelsLikesUserLikesResult } from '../__generated__/news/Api';
import { getFingerprint } from '../selectors/users';
import { deleteLike, getArticleLikes, postLike } from '../services/likes';
import { App } from '../types';

type TUserLikes = Record<number, boolean>;

export type IState = {
  userLikes: TUserLikes;
  articleLikes: {
    // articleId :  totalLikes
    [id: number]: number;
  };
  isLoading: boolean;
  error: string | null;
};

const initialState: IState = {
  userLikes: {},
  articleLikes: {},
  isLoading: false,
  error: null,
};

export const loadingLikesStart = createAction('likes/loading/start');
export const loadingLikesFailure = createAction<{ errorMessage: string }>('likes/loading/failure');
export const loadingArticleLikesSuccess = createAction<{ total: number; articleId: number }>(
  'likes/loading/articleLikes/success',
);
export const setArticleUserLikes = createAction<SravniNewsSharedModelsLikesUserLikesResult['materials']>(
  'articleUserLikes/set',
);

export const toggleArticleLikeSuccess = createAction<{ articleId: number; isLiked: boolean }>('likes/article/toggle');

export const toggleArticleLike = (articleId: number, isLiked: boolean, fingerPrint?: string): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const fp = getFingerprint(state);
    try {
      if (isLiked) {
        await deleteLike({ postId: articleId, entityId: 0, fingerPrint: fingerPrint || fp });
      } else {
        await postLike({ postId: articleId, entityId: 0, fingerPrint: fingerPrint || fp });
      }
      dispatch(toggleArticleLikeSuccess({ articleId, isLiked }));
    } catch (e) {
      const errorMessage = e?.message || 'Could not toggle article like';
      dispatch(loadingLikesFailure({ errorMessage }));
    }
  };
};

export const fetchArticleLikes = (articleId): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(loadingLikesStart());
      const total = await getArticleLikes(articleId);
      dispatch(loadingArticleLikesSuccess({ total, articleId }));
    } catch (e) {
      const errorMessage = e?.message || 'Could not fetch article likes';
      dispatch(loadingLikesFailure({ errorMessage }));
    }
  };
};

export default createReducer<IState>({}, initialState)
  .on(loadingLikesStart, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  })
  .on(loadingLikesFailure, (state, { errorMessage }) => {
    return {
      ...state,
      isLoading: false,
      error: errorMessage,
    };
  })
  .on(setArticleUserLikes, (state, materials) => {
    const articleLikes = (materials || []).reduce(
      (acc, { targetId, like }) => ({ ...acc, [targetId]: like }),
      {} as TUserLikes,
    );

    return {
      ...state,
      userLikes: articleLikes,
      isLoading: false,
    };
  })
  .on(loadingArticleLikesSuccess, (state, { articleId, total }) => {
    return {
      ...state,
      isLoading: false,
      articleLikes: {
        ...state.articleLikes,
        [articleId]: total,
      },
    };
  })
  .on(toggleArticleLikeSuccess, (state, { articleId, isLiked }) => {
    const likesInArticle = state.articleLikes[articleId];

    return {
      ...state,
      userLikes: {
        ...state.userLikes,
        [articleId]: !isLiked,
      },
      articleLikes: {
        ...state.articleLikes,
        [articleId]: isLiked ? likesInArticle - 1 : likesInArticle + 1,
      },
    };
  });
